import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon, CogIcon, ShieldCheckIcon, BellIcon, UserIcon,
  BuildingOfficeIcon, LockClosedIcon, GlobeAltIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import settingsService from '../../services/settingsService';

const schema = yup.object().shape({
  key: yup
    .string()
    .required('Setting key is required')
    .min(2, 'Key must be at least 2 characters')
    .max(100, 'Key must be less than 100 characters')
    .matches(/^[a-z_]+$/, 'Key must contain only lowercase letters and underscores'),
  value: yup.string().required('Setting value is required'),
  displayName: yup.string().max(150, 'Display name must be less than 150 characters'),
  description: yup.string().max(500, 'Description must be less than 500 characters'),
  category: yup.string().required('Category is required'),
  valueType: yup.string().required('Value type is required'),
  isPublic: yup.boolean(),
  isEditable: yup.boolean()
});

const SettingsForm = ({ setting, categories, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      key: '',
      value: '',
      displayName: '',
      description: '',
      category: 'general',
      valueType: 'string',
      isPublic: false,
      isEditable: true
    }
  });

  const watchedValueType = watch('valueType');
  const watchedValue = watch('value');

  useEffect(() => {
    if (setting) {
      setIsEditing(true);
      reset({
        key: setting.key,
        value: setting.value,
        displayName: setting.displayName || '',
        description: setting.description || '',
        category: setting.category,
        valueType: setting.valueType,
        isPublic: setting.isPublic,
        isEditable: setting.isEditable
      });
    } else {
      setIsEditing(false);
      reset({
        key: '',
        value: '',
        displayName: '',
        description: '',
        category: 'general',
        valueType: 'string',
        isPublic: false,
        isEditable: true
      });
    }
  }, [setting, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      if (isEditing) {
        await settingsService.updateSetting(setting.id, data);
        toast.success('Setting updated successfully');
      } else {
        await settingsService.createSetting(data);
        toast.success('Setting created successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving setting:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to save setting');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'security':
        return ShieldCheckIcon;
      case 'company':
        return BuildingOfficeIcon;
      case 'notifications':
        return BellIcon;
      case 'user':
        return UserIcon;
      default:
        return CogIcon;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'security':
        return 'bg-red-100 text-red-600';
      case 'company':
        return 'bg-blue-100 text-blue-600';
      case 'notifications':
        return 'bg-yellow-100 text-yellow-600';
      case 'user':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const renderValueInput = () => {
    switch (watchedValueType) {
      case 'boolean':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value
            </label>
            <select
              {...register('value')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          </div>
        );

      case 'number':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value
            </label>
            <input
              type="number"
              {...register('value')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter numeric value"
            />
          </div>
        );

      case 'json':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value (JSON)
            </label>
            <textarea
              {...register('value')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder='{"key": "value"}'
            />
            {watchedValue && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const parsed = JSON.parse(watchedValue);
                      setValue('value', JSON.stringify(parsed, null, 2));
                    } catch (e) {
                      toast.error('Invalid JSON format');
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Format JSON
                </button>
              </div>
            )}
          </div>
        );

      case 'array':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value (Comma-separated)
            </label>
            <input
              type="text"
              {...register('value')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="item1, item2, item3"
            />
          </div>
        );

      default:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value
            </label>
            <input
              type="text"
              {...register('value')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter setting value"
            />
          </div>
        );
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CogIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {isEditing ? 'Edit Setting' : 'Create New Setting'}
                </h2>
                <p className="text-sm text-gray-600">
                  {isEditing ? 'Update setting configuration' : 'Add a new system setting'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Key */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Setting Key *
                </label>
                <input
                  type="text"
                  {...register('key')}
                  disabled={isEditing}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.key ? 'border-red-300' : 'border-gray-300'
                  } ${isEditing ? 'bg-gray-100' : ''}`}
                  placeholder="setting_key_name"
                />
                {errors.key && (
                  <p className="mt-1 text-sm text-red-600">{errors.key.message}</p>
                )}
              </div>

              {/* Display Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  {...register('displayName')}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.displayName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Human readable name"
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-600">{errors.displayName.message}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  {...register('category')}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.category ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  {categories.map((category) => {
                    const CategoryIcon = getCategoryIcon(category);
                    const categoryColor = getCategoryColor(category);
                    return (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    );
                  })}
                </select>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>

              {/* Value Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value Type *
                </label>
                <select
                  {...register('valueType')}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.valueType ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="json">JSON</option>
                  <option value="array">Array</option>
                </select>
                {errors.valueType && (
                  <p className="mt-1 text-sm text-red-600">{errors.valueType.message}</p>
                )}
              </div>

              {/* Value */}
              <div className="md:col-span-2">
                {renderValueInput()}
                {errors.value && (
                  <p className="mt-1 text-sm text-red-600">{errors.value.message}</p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Describe what this setting controls..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              {/* Options */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('isPublic')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Public Setting</span>
                    <GlobeAltIcon className="ml-1 h-4 w-4 text-gray-400" />
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('isEditable')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Editable</span>
                    <LockClosedIcon className="ml-1 h-4 w-4 text-gray-400" />
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : isEditing ? 'Update Setting' : 'Create Setting'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SettingsForm; 